import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  Polyline,
  Tooltip as LeafletTooltip,
  useMap,
} from "react-leaflet";
import MarkerClusterGroup from "@changey/react-leaflet-markercluster";
import L from "leaflet";
import {
  Spin,
  Button,
  Select,
  Dropdown,
  Menu,
  Input,
  Switch,
  Popover,
  Space,
  message,
  Tooltip as AntdTooltip,
} from "antd";
import { httpService } from "../../../services/httpService.service";
import Preview from "../../pages/planning/VisitBoard/TableView/modals/Preview";
import "leaflet.heat";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import debounce from "lodash/debounce";
import "./MapComponent.scss";
import {
  ExportOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  HeatMapOutlined,
  QuestionCircleOutlined,
  SwapOutlined,
  FileExcelOutlined,
  ShareAltOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import ReactDOMServer from "react-dom/server";
import medic from "../../../Assets/Images/medic.png";
import clinic from "../../../Assets/Images/medical.png";
import community from "../../../Assets/Images/collaboration.png";
import communityCenter from "../../../Assets/Images/roommate.png";
import others from "../../../Assets/Images/others.png";
import DistanceModal from "./DistanceModal";
import CryptoJS from "crypto-js";
import axios from "axios";

const { Option } = Select;

const createIcon = (color, facilityName) => {
  const defaultIcon = new L.DivIcon({
    html: `<div style="background-color:#999; width:24px; height:24px; border-radius:50%;"></div>`,
    className: "custom-default-icon",
    iconSize: [24, 24],
  });

  let iconHtml = "";

  switch (facilityName) {
    case "Hospital":
      iconHtml = `<img src="${medic}" style="width: 30px; height: 30px;" alt="Hospital Icon" />`;
      break;
    case "Clinic":
      iconHtml = `<img src="${clinic}" style="width: 25px; height: 25px;" alt="Hospital Icon" />`;
      break;
    case "Community":
      iconHtml = `<img src="${community}" style="width: 30px; height: 30px;" alt="Hospital Icon" />`;
      break;
    case "Community Centers":
      iconHtml = `<img src="${communityCenter}" style="width: 25px; height: 25px;" alt="Hospital Icon" />`;
      break;
    case "Others":
      iconHtml = `<img src="${others}" style="width: 30px; height: 30px;" alt="Hospital Icon" />`;
      break;
    case "noFacility":
      iconHtml = ReactDOMServer.renderToString(
        <QuestionCircleOutlined
          style={{ fontSize: 20, color: "transparent" }}
        />
      );
      break;
    default:
      return defaultIcon;
  }

  const svgIcon = `
  <div style="position: relative; width: 40px; height: 60px;">
    <div style="position: absolute; top: 0; left: 0; z-index: 1;">
      <svg width="40" height="60" viewBox="0 0 40 60" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 0C9 0 0 9 0 20c0 11 20 40 20 40s20-29 20-40C40 9 31 0 20 0z" fill="${color}" />
      </svg>
    </div>
    <div style="position: absolute; top: 5px; left: 5px; z-index: 2; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center;">
      ${iconHtml}
    </div>
  </div>
`;

  return L.divIcon({
    className: "custom-pin-icon",
    html: svgIcon,
    iconSize: [40, 60],
    iconAnchor: [20, 60],
    popupAnchor: [0, -60],
  });
};

function HeatmapLayer({ points, visible }) {
  const map = useMapEvents({});
  useEffect(() => {
    if (!map || !visible) return;
    const heatPoints = points
      .map((p) => {
        if (p.locationDetails?.latitude && p.locationDetails?.longitude) {
          return [p.locationDetails.latitude, p.locationDetails.longitude, 2];
        }
        return null;
      })
      .filter(Boolean);

    const heatLayer = L.heatLayer(heatPoints, {
      radius: 30,
      blur: 20,
      maxZoom: 8,
      gradient: {
        0.4: "blue",
        0.65: "lime",
        1: "red",
      },
    });

    heatLayer.addTo(map);
    return () => map.removeLayer(heatLayer);
  }, [map, points, visible]);

  return null;
}

const statusLabels = {
  2: "Pre-planning phase",
  3: "Planning",
  4: "Data Collection",
  5: "Post data collection/Data check",
  6: "Visit completed",
};

const statusColors = {
  2: "#325899CC", // 80% opacity
  3: "#964B00CC",
  4: "#3B81FECC",
  5: "#045B46CC",
  6: "#0F7507CC",
};
const MapComponent = ({ availableStatuses, selectedIP: propSelectedIP }) => {
  const isGuestShare = process.env.REACT_APP_API_IS_GUEST;
  const [selectedStatuses, setSelectedStatuses] = useState(availableStatuses);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDistanceModal, setShowDistanceModal] = useState(false);
  const [selectedSiteVisit, setSelectedSiteVisit] = useState(null);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [allCountries, setAllCountries] = useState([]);
  const [implementingPartners, setImplementingPartners] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [filtersValue, setFiltersValue] = useState({
    Search: "",
  });
  const [selectedIP, setSelectedIP] = useState(propSelectedIP || null);
  const projectId = localStorage.getItem("currentProjectID");
  const [facility, setFacility] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [allStates, setAllStates] = useState([]); // New state for states
  const [selectedState, setSelectedState] = useState(null); // Selected state
  const [isMapPublic, setIsMapPublic] = useState(false);
  const [mapPublicLoading, setMapPublicLoading] = useState(false);

  useEffect(() => {
    if (propSelectedIP) {
      setSelectedIP(propSelectedIP);
    }
  }, [propSelectedIP]);

  const debouncedUpdateSearch = useCallback(
    debounce((value) => {
      setFiltersValue((prev) => ({
        ...prev,
        Search: value,
      }));
    }, 1000),
    []
  );

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleSearch = (event) => {
    const value = event?.target.value.toLowerCase();
    setSearchInput(value); // Update immediately for UI responsiveness
    debouncedUpdateSearch(value); // Debounced filter update
  };

  useEffect(() => {
    httpService.get(
      `/api/Site/getCountriesOfSiteVisits`,
      (res) => {
        const data = res.data;
        const formatedData = data
          .filter(
            (country) =>
              country.trim() != "string" && country.trim() != "undefined"
          )
          .map((country, index) => ({
            id: index,
            stateName: country.trim(),
          }));
        setAllCountries(formatedData);
      },
      (error) => {
        // console.log(error);
      }
    );

    const workspaceId = localStorage.getItem("currentContractId");
    const apiIps = `/api/ImplementingPartner/getAllImplementingPartners?contractId=${workspaceId}&IgnorePagination=true`;

    if (!isGuestShare) {
      httpService.get(
        apiIps,
        (res) => setImplementingPartners(res.data),
        (error) => {
          // console.log(error);
        }
      );

      // Reflect whether the project's public map is currently enabled.
      if (projectId) {
        httpService.get(
          `/api/Projects?id=${projectId}`,
          (res) => setIsMapPublic(!!res.data?.isMapPublic),
          (error) => {
            // console.log(error);
          }
        );
      }
    }

    httpService.get(
      "/api/Facility",
      (res) => {
        setFacility(res.data);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    setSelectedStatuses(availableStatuses);
  }, [availableStatuses]);

  useEffect(() => {
    fetchSiteVisits(selectedStatuses);
  }, [
    selectedCountry,
    selectedStatuses,
    selectedState,
    selectedIP,
    filtersValue?.Search,
    selectedFacility,
  ]);

  useEffect(() => {
    if (!selectedCountry) {
      setAllStates([]);
      setSelectedState(null);
      return;
    }

    httpService.get(
      `/api/Site/getStatesOfSiteVisits?country=${encodeURIComponent(
        selectedCountry
      )}`,
      (res) => {
        const data = res.data;
        const formattedStates = data
          .filter(
            (state) =>
              state && state.trim() !== "string" && state.trim() !== "undefined"
          )
          .map((state, index) => ({
            id: index,
            stateName: state.trim(),
          }));
        setAllStates(formattedStates);
        setSelectedState(null); // Reset selected state on country change
      },
      (error) => {
        console.error("Error fetching states:", error);
        setAllStates([]);
      }
    );
  }, [selectedCountry]);

  const fetchSiteVisits = (statuses) => {
    setLoading(true);
    const projectId = localStorage.getItem("currentProjectID");
    const queryParams = {
      projectId,
      pageSize: 100000,
    };

    const params = new URLSearchParams(queryParams);
    statuses.forEach((status) => params.append("SiteVisitStatus", status));

    if (selectedCountry) params.append("State", selectedCountry);
    if (selectedState) params.append("Governates", selectedState); // Assuming City param for state, adjust if needed

    if (Array.isArray(selectedFacility)) {
      selectedFacility.forEach((id) => {
        params.append("FacilityIds", id);
      });
    }
    if (selectedIP) params.append("ImplementingPartnerId", selectedIP);
    if (filtersValue?.Search) params.append("Search", filtersValue.Search);

    httpService.get(
      `/api/Site/getAllSiteVisits?${params.toString()}`,
      (res) => {
        setData(res.data);
        setLoading(false);
      },
      (error) => {
        console.error(error);
        setLoading(false);
      }
    );
  };

  const handleMarkerClick = (siteVisitCardId) => {
    setSelectedSiteVisit(siteVisitCardId);
    setShowPreviewModal(true);
  };

  const exportMap = async (format) => {
    if (!mapContainerRef.current) return;

    try {
      const canvas = await html2canvas(mapContainerRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#fff",
      });

      const imageData = canvas.toDataURL("image/png");

      if (format === "pdf") {
        const pdf = new jsPDF({ orientation: "landscape" });
        pdf.addImage(imageData, "PNG", 10, 10, 280, 150);
        pdf.save("map-export.pdf");
      } else {
        const mime = format === "jpeg" ? "image/jpeg" : "image/png";
        const link = document.createElement("a");
        link.href = canvas.toDataURL(mime);
        link.download = `map-export.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const handleClearAll = () => {
    setFiltersValue({});
    setSelectedCountry(undefined);
    setSelectedStatuses(availableStatuses);
    setSelectedIP(undefined);
    setSelectedFacility(undefined);
  };

  // Absolute, shareable link to the public map for this project.
  const getPublicMapUrl = () => {
    const payload = {
      projectId,
      publicDate: new Date().toISOString(),
    };
    const secret = "K4JW7-RRJM7-VTJJY-JV899-QHVYM";
    const encrypted = CryptoJS.AES.encrypt(
      JSON.stringify(payload),
      secret
    ).toString();
    return `${window.location.origin}/publicMap?map=${encodeURIComponent(
      encrypted
    )}`;
  };

  const openPublicMap = () => {
    window.open(getPublicMapUrl(), "_blank", "noopener,noreferrer");
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getPublicMapUrl());
      message.success("Link copied to clipboard");
    } catch (err) {
      message.error("Could not copy the link");
    }
  };

  // Enable/disable the public site-visit map for this project on the backend.
  // The guest map fetch is gated by this flag, so the share link only works
  // once the server has confirmed the map is public.
  const handleTogglePublic = (checked) => {
    if (!projectId) return;
    setMapPublicLoading(true);
    httpService.put(
      `/api/Projects/setMapPublic?projectId=${projectId}&isPublic=${checked}`,
      {},
      () => {
        setIsMapPublic(checked);
        setMapPublicLoading(false);
        message.success(
          checked ? "Public map enabled" : "Public map disabled"
        );
      },
      (error) => {
        setMapPublicLoading(false);
        message.error(
          error?.message || "Could not update the public map setting."
        );
      }
    );
  };

  const baseUrl = process.env.REACT_APP_API_BASE_URL.endsWith("/")
    ? process.env.REACT_APP_API_BASE_URL
    : `${process.env.REACT_APP_API_BASE_URL}/`;

  const exportMapToExcel = () => {
    const projectId = localStorage.getItem("currentProjectID");
    if (projectId) {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("Country", selectedCountry);
      if (selectedState) params.append("State", selectedState);
      if (filtersValue?.Search) params.append("Name", filtersValue.Search);
      if (Array.isArray(selectedFacility) && selectedFacility.length)
        selectedFacility.forEach((id) => params.append("FacilityIds", id));
      if (Array.isArray(selectedStatuses) && selectedStatuses.length)
        selectedStatuses.forEach((status) =>
          params.append("SiteVisitStatus", status)
        );
      if (selectedIP) params.append("ImplementingPartnerId", selectedIP);
      const url = `${baseUrl}api/Site/exportSiteVisitMap?projectId=${projectId}&${params.toString()}`;
      axios
        .post(url, null, {
          responseType: "blob",
        })
        .then((res) => {
          const blob = new Blob([res.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "SiteVisitMap.xlsx");
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        });
    }
  };

  return (
    <>
      <div className="filtersWrapper">
        <div className="containerFilters">
          <Input
            placeholder="Type something here"
            //   suffix={<img src={SearchIcon} alt="Search" />}
            value={filtersValue?.Search || undefined}
            onChange={handleSearch}
          />
          <Button className="clearButton" onClick={handleClearAll}>
            Clear All
          </Button>
          <Button onClick={handleToggleFilters} className="toggleButton">
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
        </div>
        {showFilters && (
          <div
            className="filtersContainer w-100"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 20,
              marginBottom: 20,
            }}
          >
            <div className="row w-100 gap-2 gap-md-3 gap-lg-5">
              <div className="col-12 col-md-5 col-lg-3">
                <p>Country</p>
                <Select
                  placeholder="Select Country"
                  showSearch
                  allowClear
                  value={selectedCountry}
                  onChange={(value) => setSelectedCountry(value)}
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) === 0
                  }
                >
                  {allCountries.map((country) => (
                    <Option key={country?.id} value={country?.stateName}>
                      {country.stateName}
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="col-12 col-md-5 col-lg-3">
                <p>State</p>
                <Select
                  placeholder="Select State"
                  showSearch
                  allowClear
                  value={selectedState}
                  onChange={(value) => setSelectedState(value)}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  disabled={!allStates.length}
                >
                  {allStates.map((state) => (
                    <Option key={state.id} value={state.stateName}>
                      {state.stateName}
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="col-12 col-md-5 col-lg-3">
                <p>Site Visit Status</p>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Select status"
                  value={selectedStatuses}
                  onChange={(value) =>
                    setSelectedStatuses(
                      value.length === 0 ? availableStatuses : value
                    )
                  }
                >
                  {availableStatuses.map((status) => (
                    <Option key={status} value={status}>
                      {statusLabels[status] || status}
                    </Option>
                  ))}
                </Select>
              </div>

              {!isGuestShare && (
                <div className="col-12 col-md-5 col-lg-3">
                  <p>Implementing Partner</p>
                  <Select
                    placeholder="Select an IP"
                    value={selectedIP}
                    onChange={(value) => setSelectedIP(value)}
                    allowClear={!propSelectedIP}
                    disabled={!!propSelectedIP}
                    style={{ width: "100%" }}
                  >
                    {implementingPartners.map((ip) => (
                      <Option key={ip?.id} value={ip?.id}>
                        {ip?.name}
                      </Option>
                    ))}
                  </Select>
                </div>
              )}

              <div className="col-12 col-md-6 col-lg-3">
                <p>Facility Type</p>
                <Select
                  placeholder="Select a facility"
                  allowClear
                  mode="multiple"
                  showSearch
                  value={selectedFacility}
                  onChange={(value) => setSelectedFacility(value)}
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {facility.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginBottom: 20,
          gap: 5,
        }}
      >
        {!isGuestShare && (
          <Popover
            trigger="click"
            placement="bottomRight"
            title="Share map"
            content={
              <div style={{ width: 320 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <span style={{ fontSize: 13, color: "#595959" }}>
                    Make this map public so anyone with the link can view it.
                  </span>
                  <Switch
                    checked={isMapPublic}
                    loading={mapPublicLoading}
                    onChange={handleTogglePublic}
                  />
                </div>

                {isMapPublic && (
                  <Space.Compact style={{ display: "flex", marginTop: 14 }}>
                    <Input
                      readOnly
                      value={getPublicMapUrl()}
                      onFocus={(e) => e.target.select()}
                      style={{ flex: 1 }}
                    />
                    <AntdTooltip title="Copy link">
                      <Button icon={<CopyOutlined />} onClick={handleCopyLink} />
                    </AntdTooltip>
                    <AntdTooltip title="Open in new tab">
                      <Button
                        icon={<ExportOutlined />}
                        onClick={openPublicMap}
                      />
                    </AntdTooltip>
                  </Space.Compact>
                )}
              </div>
            }
          >
            <Button
              type={isMapPublic ? "primary" : "default"}
              icon={<ShareAltOutlined />}
            >
              Share
            </Button>
          </Popover>
        )}
        <AntdTooltip title="Toggle Heatmap">
          <Button
            type={showHeatmap ? "primary" : "default"}
            icon={<HeatMapOutlined />}
            onClick={() => {
              setShowHeatmap((prev) => {
                return !prev;
              });
            }}
          />
        </AntdTooltip>
        <AntdTooltip title="Toggle Distances">
          <Button
            icon={<SwapOutlined />}
            onClick={() => setShowDistanceModal((prev) => !prev)}
          />
        </AntdTooltip>

        <AntdTooltip title="Export as PNG">
          <Button
            icon={<FileImageOutlined />}
            onClick={() => exportMap("png")}
          />
        </AntdTooltip>

        <AntdTooltip title="Export as PDF">
          <Button icon={<FilePdfOutlined />} onClick={() => exportMap("pdf")} />
        </AntdTooltip>

        <AntdTooltip placement="topRight" title="Export as Excel">
          <Button icon={<FileExcelOutlined />} onClick={exportMapToExcel} />
        </AntdTooltip>
      </div>
      <div ref={mapContainerRef} style={{ height: "600px", width: "100%" }}>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <MapContainer
            whenCreated={(mapInstance) => (mapRef.current = mapInstance)}
            minZoom={1}
            center={[20, 0]}
            zoom={2}
            maxBounds={[
              [-90, -180],
              [90, 180],
            ]}
            maxBoundsViscosity={1.0}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
              crossOrigin="anonymous"
            />

            {!showHeatmap && (
              <MarkerClusterGroup chunkedLoading>
                {data.map((item, idx) => (
                  <Marker
                    key={idx}
                    position={[
                      item?.locationDetails?.latitude || 0,
                      item?.locationDetails?.longitude || 0,
                    ]}
                    icon={createIcon(
                      statusColors[item?.siteVisitStatus] || "#d4217766",
                      item?.facility?.name || "noFacility"
                    )}
                    eventHandlers={{ click: () => handleMarkerClick(item.id) }}
                  >
                    <LeafletTooltip
                      direction="top"
                      offset={[0, -30]}
                      opacity={1}
                    >
                      {item.siteVisitIdentifier || item.name || "Site Visit"}
                    </LeafletTooltip>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            )}

            {showHeatmap && (
              <HeatmapLayer points={data} visible={showHeatmap} />
            )}
          </MapContainer>
        )}
      </div>
      <DistanceModal
        visible={showDistanceModal}
        onClose={() => setShowDistanceModal(false)}
      ></DistanceModal>
      {!isGuestShare && (
        <Preview
          visible={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          siteVisitCardId={selectedSiteVisit}
        />
      )}
    </>
  );
};

export default MapComponent;
